import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Star, Hammer, Layers, Wrench, Paintbrush, HardHat, ChevronRight, Phone } from 'lucide-react';
import { Button } from '../components/ui/button';
import { services, testimonials, projects, stats, companyInfo } from '../data/mock';
import MediaGallery from '../components/MediaGallery';

const iconMap = {
  Hammer,
  Layers,
  Wrench,
  Paintbrush,
  HardHat
};

const HomePage = () => {
  const featuredProjects = projects.slice(0, 4);
  const [selectedProject, setSelectedProject] = useState(null);

  const openGallery = (project) => {
    setSelectedProject(project);
  };

  const closeGallery = () => {
    setSelectedProject(null);
  };

  return (
    <div className="bg-white">
      {/* Hero Section - Dark */}
      <section className="relative min-h-screen flex items-center overflow-hidden bg-zinc-900 pb-40 lg:pb-32">
        {/* Background Image */}
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{
            // backgroundImage: `url('https://images.pexels.com/photos/585419/pexels-photo-585419.jpeg?w=1920')`,
            // backgroundImage: `url('/images/Full Renovations/10.0.jpg')`,
            backgroundImage: `url('/images/Full Renovations/6.0.jpg')`,
            // backgroundImage: `url('/images/Full Renovations/1.0.jpg')`,
          }}
        >
          <div className="absolute inset-0 bg-gradient-to-r from-zinc-900 to-zinc-900/60" />
        </div>

        {/* Orange accent bar */}
        <div className="absolute left-0 top-0 bottom-0 w-1 lg:w-2 bg-orange-500" />
        
        <div className="relative z-10 max-w-7xl mx-auto px-4 lg:px-6 py-28 lg:py-40">
          <div className="max-w-4xl">
            {/* Badge */}
            <div className="inline-flex items-center gap-3 mb-6 lg:mb-8">
              <span className="w-12 h-0.5 bg-orange-500" />
              <span className="text-orange-500 text-sm font-bold uppercase tracking-widest">Halifax & HRM</span>
            </div>
            
            {/* Main Headline */}
            <h1 className="text-4xl md:text-7xl lg:text-8xl font-black text-white leading-[0.9] mb-4 lg:mb-6 uppercase tracking-tight">
              Built Right.
              <span className="block text-orange-500">Built to Last.</span>
            </h1>
            
            {/* Subheadline */}
            <p className="text-lg md:text-2xl text-zinc-300 mb-8 lg:mb-10 max-w-2xl leading-relaxed font-medium">
              Kitchens. Floors. Drywall. Wallpaper. Full renovations. We show up, do the work, and do it right.
            </p>
            
            {/* CTAs */}
            <div className="flex flex-col sm:flex-row gap-4">
              <Link to="/projects">
                <Button className="w-full sm:w-auto bg-orange-500 hover:bg-orange-600 text-white font-black uppercase tracking-wide px-8 py-6 text-base rounded-none">
                  See Our Work
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
              </Link>
              <Link to="/contact">
                <Button variant="outline" className="w-full sm:w-auto border-2 border-white/30 text-white hover:bg-white hover:text-zinc-900 font-bold uppercase tracking-wide px-8 py-6 text-base rounded-none">
                  Get a Free Quote
                </Button>
              </Link>
            </div>
          </div>
        </div>

        {/* Stats bar at bottom */}
        <div className="absolute bottom-0 left-0 right-0 bg-white border-t-4 border-orange-500">
          <div className="max-w-7xl mx-auto px-4 lg:px-6">
            <div className="grid grid-cols-2 lg:grid-cols-4">
              {stats.map((stat, index) => (
                <div key={index} className={`py-4 lg:py-8 px-3 lg:px-4 ${index !== stats.length - 1 ? 'border-r border-zinc-200' : ''}`}>
                  <p className="text-2xl lg:text-4xl font-black text-orange-500">{stat.number}</p>
                  <p className="text-[10px] lg:text-xs text-zinc-500 uppercase tracking-wider font-bold mt-1">{stat.label}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Services Section - Light */}
      <section className="py-20 lg:py-28 bg-white">
        <div className="max-w-7xl mx-auto px-4 lg:px-6">
          {/* Section Header */}
          <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6 mb-12">
            <div>
              <div className="flex items-center gap-3 mb-4">
                <span className="w-12 h-0.5 bg-orange-500" />
                <span className="text-orange-500 text-xs font-bold uppercase tracking-widest">What We Do</span>
              </div>
              <h2 className="text-4xl lg:text-5xl font-black text-zinc-900 uppercase tracking-tight">
                Our Services
              </h2>
            </div>
            <p className="text-zinc-600 max-w-md text-base">
              From kitchen gut-jobs to flooring installs. Every project gets the same attention to detail.
            </p>
          </div>

          {/* Services Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {services.map((service) => {
              const Icon = iconMap[service.icon];
              return (
                <div
                  key={service.id}
                  className="group bg-zinc-50 border border-zinc-200 p-8 hover:bg-white hover:border-orange-300 hover:shadow-lg transition-all duration-300"
                >
                  <div className="w-14 h-14 bg-orange-500 flex items-center justify-center mb-6">
                    <Icon className="w-7 h-7 text-white" />
                  </div>
                  <h3 className="text-xl font-black text-zinc-900 uppercase tracking-tight mb-3">{service.name}</h3>
                  <p className="text-zinc-600 text-sm leading-relaxed">{service.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Featured Projects - Light with Dark Header */}
      <section className="py-20 lg:py-28 bg-zinc-100">
        <div className="max-w-7xl mx-auto px-4 lg:px-6">
          {/* Section Header */}
          <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6 mb-12">
            <div>
              <div className="flex items-center gap-3 mb-4">
                <span className="w-12 h-0.5 bg-orange-500" />
                <span className="text-orange-500 text-xs font-bold uppercase tracking-widest">Recent Work</span>
              </div>
              <h2 className="text-4xl lg:text-5xl font-black text-zinc-900 uppercase tracking-tight">
                Featured Projects
              </h2>
            </div>
            <Link
              to="/projects"
              className="group flex items-center gap-2 text-zinc-600 hover:text-orange-500 transition-colors font-bold uppercase text-sm tracking-wide"
            >
              View All Work
              <ChevronRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
            </Link>
          </div>

          {/* Projects Grid - Asymmetric */}
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">
            {/* Large project */}
            <div 
              onClick={() => openGallery(featuredProjects[0])}
              className="lg:col-span-7 group relative overflow-hidden cursor-pointer bg-white shadow-sm hover:shadow-lg transition-shadow"
            >
              <div className="overflow-hidden">
                <img
                  src={featuredProjects[0].coverImage}
                  alt={featuredProjects[0].title}
                  className="w-full h-full object-cover transform group-hover:scale-105 transition-transform duration-700"
                />
              </div>
              <div className="absolute inset-0 bg-gradient-to-t from-zinc-900 via-zinc-900/30 to-transparent" />
              {/* Hover overlay */}
              <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-colors flex items-center justify-center">
                <span className="opacity-0 group-hover:opacity-100 transition-opacity bg-orange-500 text-white font-black uppercase text-sm px-6 py-3">
                  View Gallery
                </span>
              </div>
              <div className="absolute bottom-0 left-0 right-0 p-6 lg:p-8">
                <span className="inline-block px-3 py-1 bg-orange-500 text-white text-xs font-black uppercase tracking-wide mb-3">
                  {featuredProjects[0].category}
                </span>
                <h3 className="text-2xl lg:text-3xl font-black text-white uppercase tracking-tight mb-2">{featuredProjects[0].title}</h3>
              </div>
            </div>

            {/* Stacked projects */}
            <div className="lg:col-span-5 grid grid-cols-1 gap-4">
              {featuredProjects.slice(1, 4).map((project) => (
                <div 
                  key={project.id} 
                  onClick={() => openGallery(project)}
                  className="group relative overflow-hidden cursor-pointer bg-white shadow-sm hover:shadow-lg transition-shadow"
                >
                  <div className="aspect-[16/9] overflow-hidden">
                    <img
                      src={project.coverImage}
                      alt={project.title}
                      className="w-full h-full object-cover transform group-hover:scale-105 transition-transform duration-700"
                    />
                  </div>
                  <div className="absolute inset-0 bg-gradient-to-t from-zinc-900 via-zinc-900/20 to-transparent" />
                  {/* Hover overlay */}
                  <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-colors flex items-center justify-center">
                    <span className="opacity-0 group-hover:opacity-100 transition-opacity bg-orange-500 text-white font-black uppercase text-xs px-4 py-2">
                      View Gallery
                    </span>
                  </div>
                  <div className="absolute bottom-0 left-0 right-0 p-4 lg:p-6">
                    <span className="inline-block px-2 py-0.5 bg-orange-500 text-white text-[10px] font-black uppercase tracking-wide mb-2">
                      {project.category}
                    </span>
                    <h3 className="text-lg font-black text-white uppercase tracking-tight">{project.title}</h3>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Full Screen Gallery */}
      {selectedProject && (
        <div className="fixed inset-0 z-50 bg-black">
          <MediaGallery
            media={selectedProject.media}
            projectTitle={selectedProject.title}
            onClose={closeGallery}
          />
        </div>
      )}

      {/* Testimonials - White */}
      <section className="py-20 lg:py-28 bg-white">
        <div className="max-w-7xl mx-auto px-4 lg:px-6">
          {/* Section Header */}
          <div className="mb-12">
            <div className="flex items-center gap-3 mb-4">
              <span className="w-12 h-0.5 bg-orange-500" />
              <span className="text-orange-500 text-xs font-bold uppercase tracking-widest">Testimonials</span>
            </div>
            <h2 className="text-4xl lg:text-5xl font-black text-zinc-900 uppercase tracking-tight">
              What Clients Say
            </h2>
          </div>

          {/* Testimonials Grid */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {testimonials.map((testimonial, index) => (
              <div
                key={testimonial.id}
                className={`bg-zinc-50 border border-zinc-200 p-8 ${index === 0 ? 'border-l-4 border-l-orange-500' : ''}`}
              >
                <div className="flex gap-1 mb-6">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <Star key={i} className="w-4 h-4 fill-orange-500 text-orange-500" />
                  ))}
                </div>
                <p className="text-zinc-700 leading-relaxed mb-6 text-base">"{testimonial.text}"</p>
                <div className="border-t border-zinc-200 pt-6">
                  <p className="font-black text-zinc-900 uppercase text-sm">{testimonial.name}</p>
                  <p className="text-zinc-500 text-xs uppercase tracking-wide mt-1">{testimonial.location}</p>
                  <p className="text-orange-500 text-xs font-bold uppercase tracking-wide mt-2">{testimonial.project}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section - Dark */}
      <section className="relative py-20 lg:py-24 overflow-hidden bg-zinc-900">
        <div className="relative z-10 max-w-4xl mx-auto px-4 lg:px-6 text-center">
          <h2 className="text-4xl md:text-5xl font-black text-white uppercase tracking-tight mb-6">
            Ready to Start Your Project?
          </h2>
          <p className="text-xl text-zinc-400 mb-10 max-w-2xl mx-auto">
            Free quotes. Honest pricing. Work that speaks for itself.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/contact">
              <Button className="bg-orange-500 hover:bg-orange-600 text-white font-black uppercase tracking-wide px-10 py-6 text-base rounded-none">
                Get Your Free Quote
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </Link>
            <a href={`tel:${companyInfo.phone}`}>
              <Button variant="outline" className="border-2 border-zinc-700 text-white hover:bg-white hover:text-zinc-900 font-bold uppercase tracking-wide px-10 py-6 text-base rounded-none">
                <Phone className="mr-2 w-5 h-5" />
                {companyInfo.phone}
              </Button>
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default HomePage;